// Download whole files with progress, so the journey never stalls halfway through.

export type Result = { ok: true; url: string } | { ok: false; error: unknown };

export interface Download {
  /** Settles when the file has arrived (a blob: URL) or failed; never rejects. */
  result: Promise<Result>;
  /** Bytes received so far. */
  received(): number;
  /** Stop the download (its result becomes a failure with an AbortError). */
  abort(): void;
  /** True once `result` has settled. */
  settled(): boolean;
}

export function download(url: string, onProgress: () => void): Download {
  const ctl = new AbortController();
  let got = 0;
  let isSettled = false;
  const run = async (): Promise<string> => {
    const r = await fetch(url, { signal: ctl.signal });
    if (!r.ok || !r.body) throw new Error(`${url}: HTTP ${r.status}`);
    const type = r.headers.get("content-type") || "";
    const reader = r.body.getReader();
    const parts: Uint8Array[] = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parts.push(value);
      got += value.byteLength;
      onProgress();
    }
    return URL.createObjectURL(new Blob(parts as BlobPart[], { type }));
  };
  // Handled from the start, so a failure is never an unhandled rejection.
  const result: Promise<Result> = run().then(
    (u) => ((isSettled = true), { ok: true as const, url: u }),
    (e) => ((isSettled = true), { ok: false as const, error: e }),
  );
  return { result, received: () => got, abort: () => ctl.abort(), settled: () => isSettled };
}

export function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}
